import { useCallback, useRef } from "react";
import {
  ACTIVITIES_COMBINED_SEARCH_FOCUSED,
  ACTIVITIES_COMBINED_SEARCH_TYPED,
  ACTIVITIES_COMBINED_SEARCH_RESULT_CLICKED,
  ACTIVITIES_COMBINED_SEARCH_RECENT_CLICKED,
  ACTIVITIES_COMBINED_SEARCH_CLEARED,
  ACTIVITIES_COMBINED_SEARCH_NO_RESULTS,
  ActivitiesCombinedSearchProperties,
  CombinedSearchResultType,
  CombinedSearchGroupTrackingType,
} from "redmond";
import { trackEvent } from "../api/v0/analytics/trackEvent";
import { CombinedSearchPageContext } from "../modules/search/types";

interface UseCombinedSearchAnalyticsParams {
  pageContext: CombinedSearchPageContext;
  hasRecentSearches: boolean;
}

export const useCombinedSearchAnalytics = ({
  pageContext,
  hasRecentSearches,
}: UseCombinedSearchAnalyticsParams) => {
  const hasFiredFocusRef = useRef(false);
  const hasFiredTypedRef = useRef(false);

  const resetRefs = useCallback(() => {
    hasFiredFocusRef.current = false;
    hasFiredTypedRef.current = false;
  }, []);

  const trackFocused = useCallback(() => {
    if (!hasFiredFocusRef.current) {
      hasFiredFocusRef.current = true;
      const properties: ActivitiesCombinedSearchProperties = {
        page_context: pageContext,
        has_recent_searches: hasRecentSearches,
      };
      trackEvent({
        eventName: ACTIVITIES_COMBINED_SEARCH_FOCUSED,
        properties,
      }).catch(() => {});
    }
  }, [pageContext, hasRecentSearches]);

  const trackTyped = useCallback(
    (searchQuery: string) => {
      if (!hasFiredTypedRef.current) {
        hasFiredTypedRef.current = true;
        const properties: ActivitiesCombinedSearchProperties = {
          page_context: pageContext,
          has_recent_searches: hasRecentSearches,
          search_query: searchQuery,
        };
        trackEvent({
          eventName: ACTIVITIES_COMBINED_SEARCH_TYPED,
          properties,
        }).catch(() => {});
      }
    },
    [pageContext, hasRecentSearches]
  );

  const trackResultClicked = useCallback(
    (params: {
      resultType: CombinedSearchResultType;
      resultIndex: number;
      groupType: CombinedSearchGroupTrackingType;
      searchQuery: string;
      destinationId?: string;
      destinationName?: string;
    }) => {
      const properties: ActivitiesCombinedSearchProperties = {
        result_type: params.resultType,
        result_index: params.resultIndex,
        group_type: params.groupType,
        search_query: params.searchQuery,
        page_context: pageContext,
        has_recent_searches: hasRecentSearches,
        destination_id: params.destinationId,
        destination_name: params.destinationName,
      };
      trackEvent({
        eventName: ACTIVITIES_COMBINED_SEARCH_RESULT_CLICKED,
        properties,
      }).catch(() => {});
    },
    [pageContext, hasRecentSearches]
  );

  const trackRecentClicked = useCallback(
    (resultIndex: number, searchQuery: string) => {
      const properties: ActivitiesCombinedSearchProperties = {
        result_type: CombinedSearchResultType.RecentSearch,
        result_index: resultIndex,
        group_type: "RecentSearch",
        search_query: searchQuery,
        page_context: pageContext,
        has_recent_searches: hasRecentSearches,
      };
      trackEvent({
        eventName: ACTIVITIES_COMBINED_SEARCH_RECENT_CLICKED,
        properties,
      }).catch(() => {});
    },
    [pageContext, hasRecentSearches]
  );

  const trackCleared = useCallback(() => {
    const properties: ActivitiesCombinedSearchProperties = {
      page_context: pageContext,
      has_recent_searches: hasRecentSearches,
    };
    trackEvent({
      eventName: ACTIVITIES_COMBINED_SEARCH_CLEARED,
      properties,
    }).catch(() => {});
  }, [pageContext, hasRecentSearches]);

  const trackNoResults = useCallback(
    (searchQuery: string) => {
      const properties: ActivitiesCombinedSearchProperties = {
        search_query: searchQuery,
        page_context: pageContext,
        has_recent_searches: hasRecentSearches,
      };
      trackEvent({
        eventName: ACTIVITIES_COMBINED_SEARCH_NO_RESULTS,
        properties,
      }).catch(() => {});
    },
    [pageContext, hasRecentSearches]
  );

  return {
    resetRefs,
    trackFocused,
    trackTyped,
    trackResultClicked,
    trackRecentClicked,
    trackCleared,
    trackNoResults,
  };
};
